/**
 * Normalizes onboarding summary for display.
 * Handles both founder (plain text) and investor (structured object/dict string) formats.
 * Investor summaries may arrive as JSON or Python-dict-like strings (single quotes, None/True/False).
 */

type TInvestorSummaryObj = Record<string, unknown> & {
  display_name?: string | null;
  investor_type?: string | null;
  investment_thesis?: Record<string, unknown> | null;
  check_size?: Record<string, unknown> | null;
  stages?: unknown[] | null;
  industries?: unknown[] | null;
  geo_focus?: unknown[] | null;
  ideal_portfolio_company?: Record<string, unknown> | null;
  track_record?: Record<string, unknown> | null;
};

const INVESTOR_INDICATORS = [
  "investor_type",
  "display_name",
  "investment_thesis",
  "check_size",
  "track_record",
] as const;

function hasInvestorStructure(obj: Record<string, unknown>): obj is TInvestorSummaryObj {
  return INVESTOR_INDICATORS.some((key) => key in obj && obj[key] != null);
}

const APOSTROPHE_PLACEHOLDER = "\u0001";

/**
 * Attempt to parse Python-dict-like string (single quotes, None, True, False) into a JSON-compatible string.
 * Handles escaped single quotes inside strings (\').
 */
function pythonDictLikeToJson(raw: string): string {
  let out = raw.trim();
  if (!out.startsWith("{") && !out.startsWith("[")) return raw;

  out = out
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false");
  out = out.replace(/\\'/g, APOSTROPHE_PLACEHOLDER);
  out = out.replace(/'/g, '"');
  out = out.replace(new RegExp(APOSTROPHE_PLACEHOLDER, "g"), '\\"');
  return out;
}

function tryParseStructured(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // fallthrough to Python-style parse
  }

  try {
    const asJson = pythonDictLikeToJson(trimmed);
    const parsed = JSON.parse(asJson) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // ignore
  }

  return null;
}

/** Remove markdown-style escapes that can leak from backend (e.g. SERIES\_A). */
function sanitizeForDisplay(s: string): string {
  return s.replace(/\\_/g, "_");
}

const SENTINEL_VALUES = new Set(["unknown", "n/a", "n/a.", "none", "not provided", "not available"]);

/** Returns true if the string is a placeholder the AI emits when data is absent. */
function isSentinel(s: unknown): boolean {
  return typeof s === "string" && SENTINEL_VALUES.has(s.trim().toLowerCase());
}

/** Filters out sentinel strings from an array, returning only meaningful values. */
function filterSentinels(arr: unknown[]): unknown[] {
  return arr.filter((x) => !isSentinel(x));
}

function formatList(arr: unknown[] | null | undefined): string {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  const meaningful = filterSentinels(arr);
  if (meaningful.length === 0) return "";
  return meaningful.map((x) => sanitizeForDisplay(String(x))).join(", ");
}

function formatCheckSize(checkSize: Record<string, unknown> | null | undefined): string {
  if (!checkSize || typeof checkSize !== "object") return "";
  const min = checkSize.min;
  const max = checkSize.max;
  const currency = typeof checkSize.currency === "string" ? checkSize.currency : "USD";
  if (min != null && max != null) {
    return `${formatNumber(min)} – ${formatNumber(max)} ${currency}`;
  }
  if (min != null) return `Min: ${formatNumber(min)} ${currency}`;
  if (max != null) return `Max: ${formatNumber(max)} ${currency}`;
  return "";
}

function formatNumber(n: unknown): string {
  if (typeof n !== "number") return String(n);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function formatInvestorSummary(obj: TInvestorSummaryObj): string {
  const sections: string[] = [];

  const displayName = obj.display_name ?? obj.company_name;
  if (displayName && typeof displayName === "string") {
    sections.push(displayName);
  }

  const investorType = obj.investor_type;
  if (investorType && typeof investorType === "string") {
    const friendly = sanitizeForDisplay(investorType.replace(/_/g, " "));
    sections.push(`Investor type: ${friendly}`);
  }

  const thesis = obj.investment_thesis;
  if (thesis && typeof thesis === "object") {
    const summary = (thesis as { summary?: unknown }).summary;
    if (summary && typeof summary === "string") {
      sections.push(`Investment thesis\n${sanitizeForDisplay(summary)}`);
    }
    const valueAdd = (thesis as { value_add?: unknown }).value_add;
    if (Array.isArray(valueAdd)) {
      const meaningful = filterSentinels(valueAdd);
      if (meaningful.length > 0) {
        sections.push(`Value add: ${meaningful.map((v) => sanitizeForDisplay(String(v))).join(", ")}`);
      }
    }
  }

  const checkSize = formatCheckSize(obj.check_size);
  if (checkSize) sections.push(`Check size: ${checkSize}`);

  const stages = formatList(obj.stages);
  if (stages) sections.push(`Stages: ${stages}`);

  const industries = formatList(obj.industries);
  if (industries) sections.push(`Industries: ${industries}`);

  const geoFocus = formatList(obj.geo_focus);
  if (geoFocus) sections.push(`Geography: ${geoFocus}`);

  const ideal = obj.ideal_portfolio_company;
  if (ideal && typeof ideal === "object") {
    const desc = (ideal as { description?: unknown }).description;
    if (desc && typeof desc === "string") {
      sections.push(`Ideal portfolio company\n${sanitizeForDisplay(desc)}`);
    }
    const mustHaves = (ideal as { must_haves?: unknown }).must_haves;
    if (Array.isArray(mustHaves)) {
      const meaningful = filterSentinels(mustHaves);
      if (meaningful.length > 0) {
        sections.push(`Must-haves: ${meaningful.map((v) => sanitizeForDisplay(String(v))).join(", ")}`);
      }
    }
  }

  const track = obj.track_record;
  if (track && typeof track === "object") {
    const highlights = (track as { highlights?: unknown }).highlights;
    const meaningfulHighlights = Array.isArray(highlights) ? filterSentinels(highlights) : [];
    if (meaningfulHighlights.length > 0) {
      sections.push(`Track record\n${meaningfulHighlights.map((h) => `• ${sanitizeForDisplay(String(h))}`).join("\n")}`);
    } else {
      const tr = track as { investments_count?: unknown; exits_count?: unknown; notable_portfolio?: unknown };
      const count = typeof tr.investments_count === "number" ? tr.investments_count : 0;
      const exits = typeof tr.exits_count === "number" ? tr.exits_count : 0;
      const notable = tr.notable_portfolio;
      if (count > 0 || exits > 0) {
        sections.push(`Track record: ${count} investments, ${exits} exits`);
      }
      if (Array.isArray(notable)) {
        const meaningfulNotable = filterSentinels(notable);
        if (meaningfulNotable.length > 0) {
          sections.push(`Notable portfolio: ${meaningfulNotable.map((v) => sanitizeForDisplay(String(v))).join(", ")}`);
        }
      }
    }
  }

  return sections.join("\n\n");
}

export type TSummarySection = { label?: string; content: string };

function normalizeRole(role: string | null | undefined): string {
  return (role ?? "").trim().toUpperCase();
}

/**
 * Parses a normalized summary string into sections for structured rendering.
 * Sections are split by double newlines. Labels end with ":" or are the first line of multi-line blocks.
 */
export function parseSummarySections(summary: string): TSummarySection[] {
  if (!summary?.trim()) return [];
  const blocks = summary.split(/\n\n+/).filter(Boolean);
  return blocks.map((block) => {
    const nl = block.indexOf("\n");
    if (nl === -1) {
      const colon = block.indexOf(": ");
      if (colon > 0) return { label: block.slice(0, colon), content: block.slice(colon + 2) };
      return { content: block };
    }
    const firstLine = block.slice(0, nl);
    const rest = block.slice(nl + 1).trim();
    const colon = firstLine.indexOf(": ");
    if (colon > 0) {
      const content = firstLine.slice(colon + 2) + (rest ? `\n${rest}` : "");
      return { label: firstLine.slice(0, colon), content };
    }
    return { label: firstLine, content: rest };
  });
}

export type TFounderSummaryParsed = {
  summary: string;
  recommendations: string[];
};

/**
 * Attempts to extract a structured founder summary from:
 *   - Pure JSON string: { summary, recommendation_1, recommendation_2, ... }
 *   - Mixed string: "plain text paragraph\n\n{ ...json... }"
 * Returns null when neither pattern is detected.
 */
export function parseFounderSummaryStructured(raw: string): TFounderSummaryParsed | null {
  // Find the last `{` that starts a JSON object in the string
  const jsonStart = raw.lastIndexOf("\n{");
  const candidate = jsonStart >= 0 ? raw.slice(jsonStart).trim() : raw.trim();

  let obj: Record<string, unknown> | null = null;
  try {
    const parsed = JSON.parse(candidate) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      obj = parsed as Record<string, unknown>;
    }
  } catch {
    // not JSON — try sanitizing invalid escape sequences (e.g. \' from AI output)
    try {
      const sanitized = candidate.replace(/\\'/g, "’");
      const parsed = JSON.parse(sanitized) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        obj = parsed as Record<string, unknown>;
      }
    } catch {
      // ignore
    }
  }

  if (!obj) return null;

  const summaryText = typeof obj.summary === "string" ? obj.summary.trim() : "";
  if (!summaryText) return null;

  const recommendations: string[] = [];
  let i = 1;
  while (typeof obj[`recommendation_${i}`] === "string") {
    const rec = (obj[`recommendation_${i}`] as string).trim();
    if (rec) recommendations.push(rec);
    i++;
  }

  return { summary: summaryText, recommendations };
}

/**
 * Strips backend-internal fields from founder summaries and cleans up raw markdown
 * field names that were never meant to be shown to users.
 * When an embedded JSON block is detected, the structured content replaces the raw text.
 */
function cleanFounderSummary(raw: string): string {
  // If the string contains an embedded JSON block, extract and reformat it
  const structured = parseFounderSummaryStructured(raw);
  if (structured) {
    const parts: string[] = [structured.summary];
    structured.recommendations.forEach((rec, idx) => {
      parts.push(`${idx + 1}. ${rec}`);
    });
    return parts.join("\n\n");
  }

  return raw
    .replace(/\*\*truths_stored\*\*:[\s\S]*$/, "")
    .replace(/\*\*truths_extracted_count\*\*:.*\n?/g, "")
    .replace(/\*\*recommendation_(\d+)\*\*:?/gi, "### Recommendation $1")
    .replace(/^(Recommendation \d+)\s+/gm, "### $1\n\n")
    .trim();
}

/**
 * Normalizes raw onboarding summary into a display string.
 * - Founder summaries (plain text): returned unchanged.
 * - Investor summaries (JSON or Python-dict string): parsed and formatted into readable sections.
 * - Malformed input: returned as-is.
 */
export function normalizeOnboardingSummary(raw: unknown, companyRole?: string | null): string {
  if (raw == null) return "";
  if (typeof raw !== "string") {
    if (typeof raw === "object") return JSON.stringify(raw);
    if (typeof raw === "number" || typeof raw === "boolean" || typeof raw === "bigint") return String(raw);
    return "";
  }

  const trimmed = raw.trim();
  if (!trimmed) return "";

  const role = normalizeRole(companyRole);

  // Founder/fundraiser summaries are plain text by contract — but may contain internal backend fields.
  if (role === "FUNDRAISER" || role === "FOUNDER") {
    return cleanFounderSummary(trimmed);
  }

  const obj = tryParseStructured(trimmed);
  if ((role === "INVESTOR" || !role) && obj && hasInvestorStructure(obj)) {
    try {
      return formatInvestorSummary(obj);
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}
